import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import './BlockedUsers.css';

function BlockedUsers() {
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchBlockedUsers();
  }, []);

  const fetchBlockedUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiFetch('/api/blocks', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBlockedUsers(data.blocks);
      } else {
        setMessage('Failed to load blocked users');
      }
    } catch (error) {
      console.error('Error fetching blocked users:', error);
      setMessage('Failed to load blocked users');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (userId, firstName) => {
    if (!window.confirm(`Are you sure you want to unblock ${firstName}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await apiFetch(`/api/blocks/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMessage(`${firstName} has been unblocked`);
        setBlockedUsers(blockedUsers.filter(user => user.userId !== userId));
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Failed to unblock user');
      }
    } catch (error) {
      console.error('Error unblocking user:', error);
      setMessage('Failed to unblock user');
    }
  };

  if (loading) {
    return (
      <div className="blocked-users-container">
        <div className="loading">Loading blocked users...</div>
      </div>
    );
  }

  return (
    <div className="blocked-users-container">
      <div className="blocked-users-header">
        <button onClick={() => navigate(-1)} className="back-button">
          ← Back
        </button>
        <h1>🚫 Blocked Users</h1>
        <p>Users you've blocked won't be able to see your profile or contact you.</p>
      </div>

      {message && (
        <div className={`message ${message.includes('Failed') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {blockedUsers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🎉</div>
          <h2>No Blocked Users</h2>
          <p>You haven't blocked anyone yet.</p>
          <p className="empty-hint">
            If someone makes you uncomfortable, you can block them from their profile page.
          </p>
        </div>
      ) : (
        <div className="blocked-users-list">
          {blockedUsers.map((user) => (
            <div key={user.id} className="blocked-user-card">
              <div className="user-info">
                <div className="user-avatar">
                  {user.photoUrl ? (
                    <img src={user.photoUrl} alt={user.firstName} />
                  ) : (
                    <div className="avatar-placeholder">
                      {user.firstName?.charAt(0) || '?'}
                    </div>
                  )}
                </div>
                <div className="user-details">
                  <h3>{user.firstName} {user.lastName}</h3>
                  {user.reason && (
                    <p className="block-reason">Reason: {user.reason}</p>
                  )}
                  <p className="block-date">
                    Blocked {new Date(user.blockedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleUnblock(user.userId, user.firstName)}
                className="unblock-button"
              >
                Unblock
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="info-section">
        <h3>About Blocking</h3>
        <ul>
          <li>Blocked users can't see your profile or send you messages</li>
          <li>You won't see them in discovery or search results</li>
          <li>Any existing connections will be removed</li>
          <li>Pending connection requests will be cancelled</li>
          <li>You can unblock users at any time</li>
        </ul>
      </div>
    </div>
  );
}

export default BlockedUsers;
